'use client';

import { useState, useEffect, useContext } from 'react';
import Logger from '@shared/logger';
import { RelatedDataContext } from '../types/RelatedDataContext';
import type { FormFieldMapping } from '../types/FormFieldMapping';
import type { RelatedDataConfig } from '../types/RelatedDataConfig';
import { FetchApiClient } from '@shared/httpService';

/**
 * @group Types
 * 
 * @summary
 * <blockquote>Type definition for related data state that maps keys to arrays of related entities.</blockquote>
 * 
 * @example
 * ```typescript
 * const relatedData: RelatedDataState = {
 *   'users': userList,
 *   'roles': roleList
 * };
 * ```
 */
interface RelatedDataState {
  [key: string]: any[];
}

/**
 * @group Types
 * 
 * @summary
 * <blockquote>Return type for the useRelatedDataWithContext hook containing related data, loading state, and error information.</blockquote>
 * 
 * @example
 * ```typescript
 * const result: UseRelatedDataResult = {
 *   relatedData: { 'users': userList },
 *   isLoading: false,
 *   error: null
 * };
 * ```
 */
interface UseRelatedDataResult {
  relatedData: RelatedDataState;
  isLoading: boolean;
  error: string | null;
}

export function useRelatedDataWithContext(mappings: Record<string, FormFieldMapping>): UseRelatedDataResult {
  const [relatedData, setRelatedData] = useState<RelatedDataState>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchApi = new FetchApiClient();
  
  // Check if related data is provided via context
  const contextData = useContext(RelatedDataContext);

  useEffect(() => {
    // If we have context data, use it instead of making API calls
    if (contextData) {
      setRelatedData(contextData);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Fallback to original API-based logic
    // Collect all related data configs from all mappings
    const allRelatedDataConfigs: RelatedDataConfig[] = [];
    
    Object.values(mappings).forEach(mapping => {
      if (mapping.relatedData) {
        allRelatedDataConfigs.push(...mapping.relatedData);
      }
    });

    // Remove duplicates based on key
    const uniqueConfigs = allRelatedDataConfigs.filter(
      (config, index, array) => array.findIndex(c => c.key === config.key) === index
    );

    if (uniqueConfigs.length === 0) {
      return;
    }

    setIsLoading(true);
    setError(null);

    // Fetch all related data concurrently
    const fetchPromises = uniqueConfigs.map(async (config) => {
      const result = await fetchApi.get(config.endpoint);

      if (result.isErr) {
        const error = result.getFail();
        const errorObj = new Error(error.errorMessage);
        Logger.error(`Error fetching ${config.key}`, { resource: 'data.users' }, errorObj);
        return { key: config.key, data: [], error };
      }

      const apiResponse = result.getSuccess();
      if (!apiResponse.ok) {
        const errorMessage = `Failed to fetch ${config.key} from ${config.endpoint}`;
        const errorObj = new Error(errorMessage);
        Logger.error(`Error fetching ${config.key}`, { resource: 'data.users' }, errorObj);
        return { key: config.key, data: [], error: { errorCode: 'API_ERROR', errorMessage } };
      }

      const data = apiResponse.data as any;
      const transformedData = config.transform ? config.transform(data) : data;
      return { key: config.key, data: transformedData, error: null };
    });

    Promise.all(fetchPromises)
      .then((results) => {
        const newRelatedData: RelatedDataState = {};
        let hasError = false;

        results.forEach(({ key, data, error: fetchError }) => {
          if (fetchError) {
            hasError = true;
          } else {
            newRelatedData[key] = data;
          }
        });
        setRelatedData(newRelatedData);
        if (hasError) {
          setError('Some related data failed to load');
        }
      })
      .catch((err) => {
        Logger.error('Error fetching related data', { resource: 'data.users' }, err as Error);
        setError('Failed to load related data');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [mappings, contextData]);

  return {
    relatedData,
    isLoading,
    error
  };
} 